'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { BookOpen, Clock, ShoppingCart, Check, Gift, Tag } from 'lucide-react';
import type { Course } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import { CoverPlaceholder } from '@/components/ui/CoverPlaceholder';
import { Ribbon } from '@/components/ui/Ribbon';
import { countLessons } from '@/lib/course-lessons';

interface CourseCardProps {
  course: Course;
}

function formatDuration(mins: number) {
  if (mins < 60) return `${mins} menit`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} jam ${m} mnt` : `${h} jam`;
}

function formatPrice(amount: number) {
  return 'Rp\u00a0' + amount.toLocaleString('id-ID');
}

export function CourseCard({ course }: CourseCardProps) {
  const t = useTranslations('courseCardCatalog');
  const { addItem, openDrawer, isInCart } = useCart();
  const { ownedCourseIds } = useOwnedItems();

  const accentColor = course.examTypes[0]?.color ?? '#0d9488';
  const totalLessons = countLessons(course);
  const hasDiscount = course.promoPrice != null && course.basePrice != null && course.promoPrice < course.basePrice;
  const discountPct = hasDiscount
    ? Math.round(((course.basePrice! - course.promoPrice!) / course.basePrice!) * 100)
    : 0;

  const isOwned = ownedCourseIds.has(course.id);
  const inCart = isInCart(course.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOwned || inCart) return;
    addItem({
      type: 'course',
      moduleId: course.id,
      title: course.title,
      price: course.promoPrice ?? course.basePrice ?? 0,
      basePrice: course.basePrice ?? 0,
      promoPrice: course.promoPrice ?? null,
      isFree: course.isFree,
      coverImageUrl: course.coverImageUrl,
    });
    openDrawer();
  };

  return (
    // The wrapper is not clipped, so the ribbon can overhang the card edge; the
    // Link keeps overflow-hidden for the rounded cover.
    <div className="relative h-full w-full">
    <Link
      href={`/courses/${course.slug}`}
      className="group flex flex-col h-full bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all overflow-hidden"
    >
      {/* Cover image — clean, no overlays */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
        {course.coverImageUrl ? (
          <Image
            src={course.coverImageUrl}
            alt={course.title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <CoverPlaceholder label={course.examTypes[0]?.name ?? course.title} accentColor={course.examTypes[0]?.color ?? accentColor} />
        )}
      </div>

      <div className="p-3 flex flex-col flex-1">
        {/* Badges row */}
        <div className="flex items-center gap-1 flex-wrap mb-2">
          {course.examTypes.slice(0, 1).map((et) => (
            <span
              key={et.slug}
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${et.color ?? accentColor}15`, color: et.color ?? accentColor }}
            >
              {et.name}
            </span>
          ))}
        </div>

        <h3 className="font-bold text-slate-900 text-sm leading-snug mb-2.5 line-clamp-2 group-hover:text-teal-700 transition-colors">
          {course.title}
        </h3>

        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
          <span className="flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" />
            {totalLessons} {t('materiLabel')}
          </span>
          {course.totalDurationMins > 0 && (
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {formatDuration(course.totalDurationMins)}
            </span>
          )}
        </div>

        {/* Price + Cart button */}
        <div className="pt-3 border-t border-slate-100 flex items-center justify-between mt-auto">
          {isOwned ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-teal-600">
              <Check className="w-3.5 h-3.5" />
              {t('dimiliki')}
            </span>
          ) : course.isFree ? (
            <span className="font-bold text-base text-emerald-600">{t('gratis')}</span>
          ) : hasDiscount ? (
            <span className="flex items-baseline gap-1.5 flex-wrap">
              <span className="font-bold text-base text-orange-600">{formatPrice(course.promoPrice!)}</span>
              <span className="text-xs text-slate-400 line-through">{formatPrice(course.basePrice!)}</span>
            </span>
          ) : course.basePrice != null ? (
            <span className="font-bold text-base text-slate-900">{formatPrice(course.basePrice)}</span>
          ) : (
            <span className="text-xs text-slate-400 italic">{t('hargaBelumDitetapkan')}</span>
          )}
          {isOwned ? null : inCart ? (
            <span className="flex items-center gap-1 text-xs text-teal-600 font-semibold">
              <Check className="w-3.5 h-3.5" />
              {t('diKeranjang')}
            </span>
          ) : course.basePrice != null || course.isFree ? (
            <button
              onClick={handleAddToCart}
              className={`flex items-center gap-1 text-xs font-semibold px-3 py-2 min-h-[36px] rounded-lg transition-colors ${
                course.isFree
                  ? 'text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100'
                  : 'text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100'
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              {t('daftar')}
            </button>
          ) : (
            <span className="text-xs text-teal-600 font-semibold group-hover:underline">
              {t('lihatDetail')}
            </span>
          )}
        </div>
      </div>
    </Link>
    {/* One ribbon at most: free wins over discount. An owned card shows no price. */}
    {!isOwned && course.isFree && <Ribbon tone="free" icon={Gift} label={t('gratis')} />}
    {!isOwned && !course.isFree && hasDiscount && (
      <Ribbon tone="discount" icon={Tag} label={`-${discountPct}%`} />
    )}
    </div>
  );
}
